import * as React from 'react';
import { CollapsableSection, Flex, Loading, Section, TopSection } from 'lqx-react';
import { RelationshipModel, globalStore, WorkflowStepHandlerDescriptionModel } from "api"
import { FormSelectWithDictionary } from '../common';
import { useIO, useModel } from '@type-r/react';
import { AttributesMixin, Collection, define, Store } from '@type-r/models';
import { makeStyles } from '@material-ui/core';

interface State extends AttributesMixin<typeof State> { }

@define class State extends Store {
    static attributes = {
        workflowSteps: [WorkflowStepHandlerDescriptionModel]
    }

    async fetch(workflowId: String) {
        this.workflowSteps.set(await WorkflowStepHandlerDescriptionModel.getByWorkflowId(workflowId));
        return this;
    }
}

export const WorkflowSection = ({ relationship }: { relationship: RelationshipModel }) => {
    const state = useModel(State);

    const isReady = useIO(async () => {
        await state.fetch(relationship.defaultWorkflowMap.id).catch(e => console.error(e));
    }, [relationship.$.defaultWorkflowMap]);

    return (
        <TopSection id="workflow" title="Workflow">
            <CollapsableSection title="Workflow">
                <FormSelectWithDictionary
                    label="Workflow"
                    controlDisabled={false}
                    $value={relationship.$.defaultWorkflowMap}
                    items={globalStore.workflows} />
                {
                    isReady ? (
                        <WorkflowStepSection workflowSteps={state.workflowSteps} />
                    ) : <Loading />
                }
            </CollapsableSection>
        </TopSection>
    );
}

const SETTINGS = {
    line_succes_length: 60,
    line_fail_length: 30,
    color_fail: 'red',
    color_success: 'green',
    line_width: 4
};

const styleBase = {
    node: {
        borderWidth: SETTINGS.line_width,
        borderStyle: 'solid',
        borderRadius: 10,
        padding: 10
    },
    after: {
        position: 'absolute',
        content: '""',
        width: 0,
        height: 0,
        borderWidth: 2 * SETTINGS.line_width,
        borderStyle: 'solid',
        borderColor: 'transparent',
    }
};

const useStyles = makeStyles({
    root: {
        marginBottom: 10
    },
    node: {
        ...styleBase.node,
        borderColor: '#052C4C'
    },
    success: {
        ...styleBase.node,
        borderColor: SETTINGS.color_success,
        alignSelf: 'flex-start'
    },
    fail: {
        ...styleBase.node,
        borderColor: SETTINGS.color_fail,
        alignSelf: 'center'
    },
    lineSuccess: {
        borderTopWidth: SETTINGS.line_width,
        borderTopStyle: 'solid',
        borderTopColor: SETTINGS.color_success,
        width: SETTINGS.line_succes_length - 2 * SETTINGS.line_width,
        marginTop: 25,
        marginRight: 2 * SETTINGS.line_width,
        position: 'relative',
        '&::after': {
            ...styleBase.after,
            borderLeftColor: SETTINGS.color_success,
            top: -10,
            right: -16
        }
    },
    lineFail: {
        borderLeftWidth: SETTINGS.line_width,
        borderLeftStyle: 'solid',
        borderLeftColor: SETTINGS.color_fail,
        marginBottom: 2 * SETTINGS.line_width,
        height: SETTINGS.line_fail_length - 2 * SETTINGS.line_width,
        alignSelf: 'center',
        position: 'relative',
        '&::after': {
            ...styleBase.after,
            borderTopColor: SETTINGS.color_fail,
            bottom: -16,
            left: -10
        }
    }
});

const buildNode = (id, desc, success, fail, compiledStyles) => {
    return (
        <Flex row className={compiledStyles.root} key={id}>
            <Flex column>
                <div className={compiledStyles.node}>{desc}</div>
                <div className={compiledStyles.lineFail}></div>
                <div className={compiledStyles.fail}>{fail}</div>
            </Flex>
            <div className={compiledStyles.lineSuccess}></div>
            <div className={compiledStyles.success}>{success}</div>
        </Flex>
    );
}

const DEFAULT_WORKFLOW_STEP_HANDLER = 1;
const tryParseJSONObject =  (jsonString) =>{
    try {
        var o = JSON.parse(jsonString);
        if (o && typeof o === "object") {
            return o;
        }
    }
    catch (e) { }

    return false;
};

const buildDescription = (ws:WorkflowStepHandlerDescriptionModel) =>{
    if(ws.ehId != DEFAULT_WORKFLOW_STEP_HANDLER) return ws.description;
    var schema = tryParseJSONObject(ws.jsonSchema);
    return (schema 
        && schema.properties
        && schema.properties.processor_type
        && schema.properties.processor_type.enum) ?
        schema.properties.processor_type.enum[0] :
        ws.description;
}

const buildSuccessFail = (allWorkflowSteps, ws: WorkflowStepHandlerDescriptionModel, compiledStyles) => {
    var childWorkflowSteps = allWorkflowSteps.filter(x => x.ehId == ws.ehSuccessId);
    return buildNode(ws.id,
        buildDescription(ws),
        childWorkflowSteps.length ? childWorkflowSteps.map(x => buildSuccessFail(allWorkflowSteps, x, compiledStyles)) : ws.ehSuccessDescription,
        ws.ehFailDescription,
        compiledStyles);
}

export const WorkflowStepSection = ({ workflowSteps }: { workflowSteps: Collection<WorkflowStepHandlerDescriptionModel> }) => {
    if (!workflowSteps || !workflowSteps.length) return (<p>No steps configured</p>);

    var start = workflowSteps.filter(x => x.ehId == DEFAULT_WORKFLOW_STEP_HANDLER);
    if (!start.length) return (<p>No Default steps configured.</p>);

    const compiledStyles = useStyles();
    return (
        <Section title="Workflow Steps">
            <br />
            <Flex column center>
                {start.map(x => buildSuccessFail(workflowSteps, x, compiledStyles))}
            </Flex>
        </Section>
    );
}